import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, MessageCircle, Users, MapPin } from 'lucide-react';
import api from '@/lib/api';
import { waLink, SUMBER_OPTIONS, LEAD_STATUSES } from '@/lib/format';
import { LeadBadge } from '@/components/StatusBadge';
import { ListSkeleton } from '@/components/Skeletons';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';

const EMPTY = { nama: '', whatsapp: '', alamat: '', sumber: 'WhatsApp', status: 'Baru', catatan: '' };

export default function Pelanggan() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      const r = await api.get('/customers', { params });
      setItems(r.data);
    } finally { setLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const save = async () => {
    if (!form.nama.trim()) { toast.error('Nama wajib diisi'); return; }
    setSaving(true);
    try {
      await api.post('/customers', form);
      toast.success('Pelanggan ditambahkan');
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { !e?.__gated && toast.error('Gagal menyimpan'); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-xl font-bold">Pelanggan & Lead</h1>
          <p className="text-sm text-muted-foreground">Kelola calon & pelanggan Anda.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="pelanggan-tambah-button" className="rounded-xl"><Plus className="mr-1.5 h-4 w-4" /> Tambah</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">Tambah Pelanggan / Lead</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Nama *</Label><Input data-testid="pelanggan-form-nama" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="Nama pelanggan" /></div>
              <div className="space-y-1.5"><Label>No. WhatsApp</Label><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="0812xxxx" /></div>
              <div className="space-y-1.5"><Label>Alamat</Label><Input value={form.alamat} onChange={(e) => setForm({ ...form, alamat: e.target.value })} placeholder="Alamat" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Sumber</Label>
                  <Select value={form.sumber} onValueChange={(v) => setForm({ ...form, sumber: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{SUMBER_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Catatan</Label><Textarea value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} placeholder="Kebutuhan pelanggan..." /></div>
            </div>
            <DialogFooter>
              <Button data-testid="pelanggan-form-simpan" onClick={save} disabled={saving} className="rounded-xl">Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="crm-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama / WA / alamat" className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36" data-testid="crm-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? <ListSkeleton /> : items.length === 0 ? (
        <EmptyState icon={Users} title="Belum ada pelanggan" description="Tambah lead dari WhatsApp atau walk-in agar tidak hilang di chat." actionLabel="Tambah Pelanggan" onAction={() => setOpen(true)} testid="pelanggan-empty-add" />
      ) : (
        <div className="space-y-2.5">
          {items.map((c) => (
            <div key={c.id} data-testid="crm-customer-row" className="flex items-center gap-3 rounded-xl border bg-card p-3.5 transition-shadow hover:shadow-[var(--shadow-md)]">
              <button onClick={() => navigate(`/pelanggan/${c.id}`)} className="min-w-0 flex-1 text-left">
                <div className="flex items-center gap-2">
                  <span className="truncate font-semibold">{c.nama}</span>
                  <LeadBadge status={c.status} />
                </div>
                <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="h-3 w-3" /> <span className="truncate">{c.alamat || 'Alamat belum diisi'}</span> · {c.sumber}
                </div>
              </button>
              {c.whatsapp && (
                <a data-testid="crm-whatsapp-button" href={waLink(c.whatsapp)} target="_blank" rel="noreferrer"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent hover:bg-accent/20">
                  <MessageCircle className="h-5 w-5" />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
